// Client-side resize before upload. Keeps tokens (and bandwidth) low while
// preserving enough detail for the vision model.
//
// Long-edge presets per use-case:
//   - meal photo: 1536px — food texture/sauce detail improves recognition
//   - menu photo: 2048px — small printed text needs OCR-grade detail
//   - pantry photo / default: 1024px — labels are large, 1024 is plenty
const DEFAULT_MAX_EDGE_PX = 1024;
const DEFAULT_JPEG_QUALITY = 0.85;

export type CompressOptions = {
  /** Long-edge cap in pixels. Image is never upscaled. */
  maxEdge?: number;
  /** JPEG quality 0..1. */
  quality?: number;
};

export async function fileToCompressedDataUrl(
  file: File,
  options: CompressOptions = {},
): Promise<string> {
  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE_PX;
  const quality = options.quality ?? DEFAULT_JPEG_QUALITY;
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = bitmap;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(bitmap, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    bitmap.close();
  }
}
